import React           from 'react';
import { observer }    from 'mobx-react-lite';
import View            from '@widesk-ui/components/View';
import Spin            from '@widesk-ui/components/Spin';
import useDesignTokens from '@/hooks/useDesignTokens';
import planImportStore from '@/TokamapEditor/planImport/planImportStore';

const STEPS = [
	{ key: 'shapes', label: 'Création des formes et des POI' },
	{ key: 'reload', label: 'Chargement de la carte' },
] as const;

const PlanGeneratingOverlay = observer(() => {
	const tokens = useDesignTokens();
	const generating = planImportStore.generating;
	const locked = !!generating;

	React.useEffect(() => {
		if (!locked) return;

		const swallow = (event: KeyboardEvent) => {
			event.preventDefault();
			event.stopImmediatePropagation();
		};
		window.addEventListener('keydown', swallow, true);

		return () => window.removeEventListener('keydown', swallow, true);
	}, [locked]);

	if (!generating) return null;

	const currentIndex = STEPS.findIndex(step => step.key === generating);

	return (
		<div
			role="alertdialog"
			aria-busy="true"
			aria-label="Génération des formes en cours"
			style={{
				position: 'fixed',
				inset: 0,
				zIndex: 2000,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				background: `color-mix(in srgb, ${tokens.text.default} 35%, transparent)`,
				cursor: 'progress',
			}}
		>
			<View
				gap={16}
				style={{
					width: 'min(380px, calc(100vw - 32px))',
					padding: 24,
					borderRadius: 12,
					background: tokens.surface.elevated,
					boxShadow: tokens.shadow.elevated,
				}}
			>
				<View row gap={12} style={{ alignItems: 'center' }}>
					<Spin size="large" />
					<View gap={2}>
						<View size={16} style={{ fontWeight: 600 }}>Import en cours</View>
						<View size={12} color={tokens.text.secondary}>La carte est verrouillée jusqu’à la fin.</View>
					</View>
				</View>

				<View gap={8}>
					{STEPS.map((step, index) => {
						const done = index < currentIndex;
						const current = index === currentIndex;

						return (
							<View key={step.key} row gap={8} style={{ alignItems: 'center' }}>
								<View
									center
									size={11}
									style={{
										width: 18,
										height: 18,
										borderRadius: '50%',
										fontWeight: 600,
										background: done || current ? tokens.color.primary : tokens.fill.secondary,
										color: done || current ? tokens.surface.card : tokens.text.secondary,
									}}
								>
									{done ? '✓' : index + 1}
								</View>
								<View
									size={13}
									color={current ? tokens.text.default : tokens.text.secondary}
									style={{ fontWeight: current ? 600 : 400 }}
								>
									{step.label}{current ? '…' : ''}
								</View>
							</View>
						);
					})}
				</View>
			</View>
		</div>
	);
});

export default PlanGeneratingOverlay;
